// ReceivableDialogRedux.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import CustomSelect from '../../components/CustomInput';
// import { saveApiData } from 'src/store/Slice/Global/GlobalSlice';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { ReceiveApi } from '../../utils/Apis';

/**
 * Props:
 * - openPayment (bool)
 * - setOpenPayment (fn)
 * - userId (number) optional (default 146)
 * - invoiceId (number) optional (default 1000)
 * - submitPaymentAction (function) REQUIRED: Redux action creator (thunk) to dispatch
 *
 * submitPaymentAction will be called as: dispatch(submitPaymentAction(payload))
 */
export default function ReceivableDialogRedux({
  openPayment,
  setOpenPayment,
  userId,
  invoiceId,
  submitPaymentAction, // action creator / thunk from your slice
  onSuccess
}) {
  const dispatch = useDispatch();

  console.log(invoiceId, 'invoiceId222');

  // Payment mode labels (sent as exact strings)
  const PaymentMode = ['RTGS', 'NEFT', 'OTHER', 'UPI', 'BANK TRANSFER'];
  const paymentModeOptions = PaymentMode.map((pm) => ({
    label: pm,
    // sending label as value to keep API-friendly strings like "BANK TRANSFER"
    value: pm
  }));

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      receivedOn: '',
      paymentMode: '',
      netAmountReceived: '',
      gstCharged: '',
      tdsDeduction: '',
      netAmountForCommission: ''
    }
  });

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data) => {
    // build payload matching your curl example
    const payload = {
      userId,
      invoiceId,
      receivedOn: data.receivedOn, // YYYY-MM-DD
      paymentMode: data.paymentMode, // "NEFT", "BANK TRANSFER", etc.
      netAmountReceived: parseFloat(data.netAmountReceived),
      gstCharged: parseFloat(data.gstCharged),
      tdsDeduction: parseFloat(data.tdsDeduction),
      netAmountForCommission: parseFloat(data.netAmountForCommission)
    };

    // Validate numeric conversion
    if (
      Number.isNaN(payload.netAmountReceived) ||
      Number.isNaN(payload.gstCharged) ||
      Number.isNaN(payload.tdsDeduction) ||
      Number.isNaN(payload.netAmountForCommission)
    ) {
      // simple client-side guard
      alert('Please enter valid numeric values for all amount fields.');
      return;
    }

    // if (!submitPaymentAction || typeof submitPaymentAction !== 'function') {
    //   console.error('submitPaymentAction prop is missing or not a function');
    //   alert('Submit action not provided. Please wire submitPaymentAction prop.');
    //   return;
    // }

    setSubmitting(true);
    try {
      // dispatch your Redux action (thunk). you will handle API call / errors in the thunk.
      dispatch(saveApiData(ReceiveApi?.invoicePaidApi, payload));
      // optional: you might want to wait for a result if your thunk returns a promise;
      // but I won't await here — your slice can handle notifications / success state.

      // do local success UX
      reset();
      setOpenPayment(false);
      onSuccess?.();
    } catch (err) {
      console.error('dispatch error', err);
      // your thunk should handle errors, but we show a fallback alert
      console.log('Failed to dispatch submit action: ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={openPayment} onClose={() => setOpenPayment(false)} fullWidth>
      <DialogTitle className="popupTitle">
        <Typography variant="h6">Receivable</Typography>
        <IconButton onClick={() => setOpenPayment(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  Received On
                </Typography>

                <Controller
                  name="receivedOn"
                  control={control}
                  rules={{ required: 'Received On is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      inputProps={{ className: 'textInput' }}
                      error={!!errors.receivedOn}
                      helperText={errors.receivedOn?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  Select Option
                </Typography>

                <Controller
                  name="paymentMode"
                  control={control}
                  rules={{ required: 'Payment mode is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      displayEmpty
                      // make Select show placeholder when empty
                      renderValue={(selected) =>
                        selected ? selected : <span style={{ color: '#888' }}>Select option</span>
                      }
                    >
                      <MenuItem value="RTGS">RTGS</MenuItem>
                      <MenuItem value="NEFT">NEFT</MenuItem>
                      <MenuItem value="OTHER">OTHER</MenuItem>
                      <MenuItem value="UPI">UPI</MenuItem>
                      <MenuItem value="BANK TRANSFER">BANK TRANSFER</MenuItem>
                    </Select>
                  )}
                />
                {errors.paymentMode && (
                  <Typography variant="caption" color="error">
                    {errors.paymentMode.message}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  Net Amount Received
                </Typography>

                <Controller
                  name="netAmountReceived"
                  control={control}
                  rules={{
                    required: 'Net Amount Received is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.netAmountReceived}
                      helperText={errors.netAmountReceived?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  GST Charged
                </Typography>

                <Controller
                  name="gstCharged"
                  control={control}
                  rules={{
                    required: 'GST Charged is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.gstCharged}
                      helperText={errors.gstCharged?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  TDS Deduction
                </Typography>

                <Controller
                  name="tdsDeduction"
                  control={control}
                  rules={{
                    required: 'TDS Deduction is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.tdsDeduction}
                      helperText={errors.tdsDeduction?.message}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box>
                <Typography variant="h6" className="formLabel">
                  Net Amount for Commission
                </Typography>

                <Controller
                  name="netAmountForCommission"
                  control={control}
                  rules={{
                    required: 'Net Amount for Commission is required',
                    validate: (v) =>
                      (v !== '' && !Number.isNaN(Number(v))) || 'Enter a valid number'
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      inputProps={{ className: 'textInput' }}
                      type="number"
                      error={!!errors.netAmountForCommission}
                      helperText={errors.netAmountForCommission?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPayment(false)} variant="outlined" disabled={submitting}>
            Cancel
          </Button>

          <Button type="submit" variant="contained" className="mainBtn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
